/**
 * Admin Page
 * Administrative interface for user and system management
 */

import React, { useState } from 'react';
import { useRequireAdmin } from '../hooks/useAuth';
import { UserManagement } from '../components/Admin/UserManagement';
import { CreateUser } from '../components/Admin/CreateUser';
import { BatchList } from '../components/Admin/BatchStatus';
import FontManagement from './Admin/FontManagement';

type TabMode = 'users' | 'create-user' | 'batches' | 'fonts';

export const Admin: React.FC = () => {
  // Ensure user is admin
  useRequireAdmin();

  const [tabMode, setTabMode] = useState<TabMode>('users');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Admin Panel
        </h1>
        <p className="text-gray-600">
          Manage users, monitor batch processing, and configure system settings.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTabMode('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              tabMode === 'users'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              User Management
            </div>
          </button>

          <button
            onClick={() => setTabMode('create-user')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              tabMode === 'create-user'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
              Create User
            </div>
          </button>

          <button
            onClick={() => setTabMode('batches')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              tabMode === 'batches'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
              Batch Status
            </div>
          </button>
<button            onClick={() => setTabMode("fonts")}            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${              tabMode === "fonts"                ? "border-purple-500 text-purple-600"                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"            }`}          >            <div className="flex items-center gap-2">              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />              </svg>              Fonts            </div>          </button>
        </nav>
      </div>

      {/* Content */}
      <div>
        {tabMode === 'users' && <UserManagement />}

        {tabMode === 'create-user' && (
          <div className="max-w-2xl">
            <CreateUser
              onSuccess={() => {
                // Optionally switch to users tab after creation
                setTimeout(() => setTabMode('users'), 3000);
              }}
            />
          </div>
        )}

        {tabMode === 'batches' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Recent Batch Uploads
            </h2>
            <BatchList />
          </div>
        )}

        {tabMode === "fonts" && <FontManagement />}
      </div>
    </div>
  );
};
